// src/pages/Home.tsx - PREMIUM MODERN CREATIVE VERSION
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { StatsCounter } from "@/components/StatsCounter";
import { PartnerGrid } from "@/components/PartnerGrid";
import { TestimonialCard } from "@/components/TestimonialCard";
import { statsDisplay } from "@/data/mock/stats";
import { sports } from "@/data/mock/sports";
import { partners } from "@/data/mock/partners";
import { testimonials } from "@/data/mock/testimonials";
import { news } from "@/data/mock/news";
import { StoriesTray } from "@/components/stories/StoriesTray";
import { HomeImageSlider } from "@/components/HomeImageSlider";
import {
  ArrowRight,
  Trophy,
  Users,
  Globe,
  ShieldCheck,
  Award,
  Target,
  Sparkles,
  Heart,
  TrendingUp,
  Calendar,
  MapPin,
  Zap,
  Star,
  Crown,
  Flame,
  Rocket,
  Medal,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Play,
  ArrowUpRight,
  Shield
} from "lucide-react";
import { useRef, useState } from "react";

const Home = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const heroRef = useRef<HTMLDivElement>(null);
  const sportsRef = useRef<HTMLDivElement>(null);
  const [hoveredSport, setHoveredSport] = useState<number | null>(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroOpacity = useTransform(heroScroll, [0, 0.5], [1, 0]);
  const heroScale = useTransform(heroScroll, [0, 0.5], [1, 1.2]);
  const heroY = useTransform(heroScroll, [0, 1], [0, 200]);

  const DirectionalArrow = ({ className = "" }: { className?: string }) =>
    isRTL ? <ChevronLeft className={className} /> : <ChevronRight className={className} />;

  const navLabel = (ar: string, en: string) => (isRTL ? ar : en);

  // Enhanced content with more details
  const content = {
    hero: {
      badge: { ar: "منذ 2015 - رواد التميز الرياضي", en: "Since 2015 - Pioneers of Athletic Excellence" },
      title: { ar: "نبني الأبطال، نصنع المستقبل", en: "Building Champions, Crafting the Future" },
      subtitle: {
        ar: "أكاديمية رياضية متعددة التخصصات تجمع بين المعايير العالمية والخبرة المحلية لتطوير مواهب الشباب الفلسطيني",
        en: "A multi-disciplinary sports academy combining global standards with local expertise to develop Palestinian youth talent",
      },
      stats: [
        { icon: Trophy, value: "12", label: { ar: "بطولة", en: "Championships" } },
        { icon: Users, value: "10000+", label: { ar: "لاعب", en: "Players" } },
        { icon: MapPin, value: "5", label: { ar: "فروع", en: "Branches" } },
        { icon: Globe, value: "4", label: { ar: "دول", en: "Countries" } },
      ],
    },
    whyUs: {
      badge: { ar: "لماذا نحن؟", en: "Why Us?" },
      title: { ar: "ما يجعلنا مميزين", en: "What Makes Us Stand Out" },
      subtitle: {
        ar: "تجربة تدريب متكاملة تجمع بين الجودة والاحترافية والأمان",
        en: "Complete training experience combining quality, professionalism, and safety",
      },
      features: [
        {
          icon: ShieldCheck,
          title: { ar: "بيئة آمنة", en: "Safe Environment" },
          desc: {
            ar: "نوفر بيئة آمنة تحفّز اللاعبين على تطوير مهاراتهم بثقة مع مرافق حديثة ومعدات عالمية",
            en: "We provide a safe environment that motivates players to develop confidently with modern facilities and world-class equipment",
          },
          image: "/assets/images/s-blob-v1-IMAGE-ajQSFXLJsfw.jpeg",
          gradient: "from-blue-500/20 to-cyan-500/20",
        },
        {
          icon: Award,
          title: { ar: "برنامج تدريبي احترافي", en: "Professional Training Program" },
          desc: {
            ar: "منهجية تدريب حديثة مستوحاة من أفضل الأكاديميات العالمية مع شراكات استراتيجية دولية",
            en: "Modern training methodology inspired by top global academies with strategic international partnerships",
          },
          image: "/assets/images/s-blob-v1-IMAGE-HiJ0_LRWIVo.jpeg",
          gradient: "from-amber-500/20 to-orange-500/20",
        },
        {
          icon: Target,
          title: { ar: "برامج لامنهجية متكاملة بأنشطة وفعاليات", en: "Integrated Extracurricular Programs with Activities & Events" },
          desc: {
            ar: "برامج لامنهجية شاملة تتضمن أنشطة وفعاليات متنوعة لتطوير شخصية اللاعبين",
            en: "Comprehensive extracurricular programs with diverse activities and events to develop players' personalities",
          },
          image: "/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg",
          gradient: "from-green-500/20 to-emerald-500/20",
        },
        {
          icon: Users,
          title: { ar: "برامج احترافية متعددة", en: "Multiple Professional Programs" },
          desc: {
            ar: "برامج احترافية متعددة ومتنوعة تلبي احتياجات جميع الفئات العمرية والمستويات",
            en: "Multiple diverse professional programs meeting the needs of all age groups and levels",
          },
          image: "/assets/images/s-blob-v1-IMAGE-LIWinNKC_d4.jpeg",
          gradient: "from-purple-500/20 to-pink-500/20",
        },
      ],
    },
    successStories: {
      badge: { ar: "قصص نجاح", en: "Success Stories" },
      title: { ar: "أبطال صنعوا الفرق", en: "Champions Who Made a Difference" },
      subtitle: {
        ar: "لاعبون بدأوا رحلتهم معنا ووصلوا إلى مستويات عالمية واحترافية",
        en: "Players who started with us and reached global and professional levels",
      },
      stories: [
        {
          name: { ar: "محمد أحمد", en: "Mohammed Ahmed" },
          role: { ar: "منتخب فلسطين تحت 19", en: "Palestine U-19 National Team" },
          sport: { ar: "كرة القدم", en: "Football" },
          image: "/assets/team/s-blob-v1-IMAGE-AdrJGHuqoHQ.jpeg",
          quote: {
            ar: "تشامبيونز لم تعلمني فقط المهارات، بل علمتني معنى الانضباط والعمل الجماعي",
            en: "Champions didn't just teach me skills, it taught me the meaning of discipline and teamwork",
          },
          achievement: { ar: "3 بطولات محلية", en: "3 Local Championships" },
        },
        {
          name: { ar: "أحمد خالد", en: "Ahmed Khaled" },
          role: { ar: "بطل فلسطين للسباحة", en: "Palestine Swimming Champion" },
          sport: { ar: "السباحة", en: "Swimming" },
          image: "/assets/team/s-blob-v1-IMAGE-ErIcjKSeec4.jpeg",
          quote: {
            ar: "البرامج التدريبية الحديثة والدعم النفسي جعلاني أحقق المستحيل",
            en: "Modern training programs and psychological support made me achieve the impossible",
          },
          achievement: { ar: "رقم قياسي وطني", en: "National Record" },
        },
        {
          name: { ar: "يوسف إبراهيم", en: "Yousef Ibrahim" },
          role: { ar: "منتخب الشباب", en: "Youth National Team" },
          sport: { ar: "كرة السلة", en: "Basketball" },
          image: "/assets/team/s-blob-v1-IMAGE-oNCoErmyHAo.jpeg",
          quote: {
            ar: "المدربون هنا لا يدربونك فقط، بل يؤمنون بإمكانياتك ويساعدونك لتصل للقمة",
            en: "Coaches here don't just train you, they believe in your potential and help you reach the top",
          },
          achievement: { ar: "أفضل لاعب شاب", en: "Best Young Player" },
        },
      ],
    },
    impact: {
      badge: { ar: "الأثر", en: "Impact" },
      title: { ar: "إنجازاتنا بالأرقام", en: "Our Achievements in Numbers" },
      subtitle: {
        ar: "أرقام حقيقية تعكس التزامنا المستمر بالتميز والتطوير",
        en: "Real numbers reflecting our continuous commitment to excellence and development",
      },
    },
  };


  return (
    <div className="min-h-screen bg-background">
{/* ==================== STORIES TRAY (top, under header) ==================== */}
<StoriesTray />

{/* ==================== TOP IMAGE SLIDER ==================== */}
<HomeImageSlider />

{/* ==================== IMPROVED HERO SECTION ==================== */}
<section
  ref={heroRef}
  className="relative min-h-screen flex items-center justify-center overflow-hidden"
>
  {/* ENHANCED Parallax Background with STRONGER Overlay */}
  <motion.div
    style={{ scale: heroScale, y: heroY }}
    className="absolute inset-0"
  >
    {/* Background Image */}
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url(/assets/hero/hello.png)" }}
    />
    
    {/* STRONGER Multi-Layer Overlay - يخفي التشويش تمامًا */}
    <div className="absolute inset-0 bg-background/96" />
    <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(0,0,0,0.6)_100%)]" />
  </motion.div>

  {/* SIMPLIFIED Gradient Orbs - أقل ازدحامًا */}
  <motion.div
    animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3],
    }}
    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
    className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-primary/25 rounded-full blur-3xl"
  />
  <motion.div
    animate={{
      scale: [1.1, 1, 1.1],
      opacity: [0.25, 0.4, 0.25],
    }}
    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
    className="absolute bottom-1/3 right-1/3 w-[500px] h-[500px] bg-accent/20 rounded-full blur-3xl"
  />

  {/* Hero Content - IMPROVED Spacing & Hierarchy */}
  <div className="container mx-auto px-4 relative z-10 text-center py-20">
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Badge - CLEANER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mb-10"
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary/15 border border-primary/40 backdrop-blur-xl shadow-2xl shadow-primary/20"
        >
          <Crown className="h-5 w-5 text-primary" />
          <span className="text-sm font-bold text-primary tracking-wide">
            {content.hero.badge[isRTL ? "ar" : "en"]}
          </span>
          <Sparkles className="h-5 w-5 text-primary" />
        </motion.div>
      </motion.div>

      {/* Main Headline - BETTER Readability */}
      <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 leading-[1.08] max-w-5xl mx-auto">
        <motion.span
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="block"
        >
          {/* أقوى تباين للنص */}
          <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent drop-shadow-[0_0_40px_rgba(255,184,0,0.3)]">
            {navLabel("نبني الأبطال", "Building Champions")}
          </span>
        </motion.span>
        <motion.span
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="block mt-3"
        >
          <span className="bg-gradient-to-r from-accent via-primary to-primary bg-clip-text text-transparent drop-shadow-[0_0_40px_rgba(255,184,0,0.3)]">
            {navLabel("نصنع المستقبل", "Crafting the Future")}
          </span>
        </motion.span>
      </h1>

      {/* Subtitle - SHORTER & CLEARER */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
        className="text-xl sm:text-2xl text-foreground/80 mb-14 max-w-3xl mx-auto leading-relaxed font-medium"
      >
        {navLabel(
          "أكاديمية رياضية متعددة التخصصات بمعايير عالمية",
          "Multi-sport academy with international standards"
        )}
      </motion.p>

      {/* CTA Buttons - BETTER Spacing */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.7 }}
        className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-20"
      >
        <Link to="/sports">
          <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.96 }}>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-14 py-7 rounded-full border-2 border-primary/30 hover:border-primary/60 backdrop-blur-sm"
            >
              <span className="flex items-center gap-3">
                <Trophy className="h-5 w-5" />
                {navLabel("استكشف الرياضات", "Explore Sports")}
              </span>
            </Button>
          </motion.div>
        </Link>
      </motion.div>

      {/* ENHANCED Stats Grid - BIGGER & CLEARER */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.9 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-5xl mx-auto"
      >
        {content.hero.stats.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 + idx * 0.1 }}
            whileHover={{ scale: 1.08, y: -8 }}
            className="group"
          >
            <div className="p-6 rounded-2xl bg-card/90 backdrop-blur-xl border border-border/60 hover:border-primary/60 shadow-2xl hover:shadow-primary/20 transition-all duration-500">
              {/* Icon on Top */}
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="mx-auto w-fit mb-4 p-3.5 rounded-full bg-primary/15 border border-primary/30 group-hover:bg-primary/20 transition-colors"
              >
                <stat.icon className="h-8 w-8 text-primary" />
              </motion.div>

              {/* Value - BIGGER */}
              <div className="text-4xl sm:text-5xl font-bold mb-2 bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">
                {stat.value}
              </div>

              {/* Label - CLEARER */}
              <div className="text-sm sm:text-base text-muted-foreground font-medium">
                {stat.label[isRTL ? "ar" : "en"]}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  </div>

  {/* SIMPLIFIED Scroll Indicator */}
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 1.3 }}
    className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
  >
    <motion.div
      animate={{ y: [0, 10, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className="flex flex-col items-center gap-3"
    >
      <div className="w-7 h-11 border-2 border-primary/50 rounded-full flex justify-center pt-2">
        <motion.div
          animate={{ y: [0, 14, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-1.5 h-2 bg-primary rounded-full"
        />
      </div>
      <span className="text-xs text-muted-foreground font-medium">
        {navLabel("اسحب للأسفل", "Scroll Down")}
      </span>
    </motion.div>
  </motion.div>
</section>


      {/* ==================== ENHANCED STATS SECTION ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 via-background to-surface-1 relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(var(--primary-rgb),0.08)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(var(--accent-rgb),0.08)_0%,transparent_60%)]" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <TrendingUp className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {content.impact.badge[isRTL ? "ar" : "en"]}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {content.impact.title[isRTL ? "ar" : "en"]}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {content.impact.subtitle[isRTL ? "ar" : "en"]}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {statsDisplay.map((stat, idx) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  delay: idx * 0.1,
                }}
              >
                <StatsCounter
                  value={stat.value}
                  label={t(stat.labelKey)}
                  delay={idx * 0.1}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ENHANCED WHY CHOOSE US ==================== */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-surface-1 to-background" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {content.whyUs.badge[isRTL ? "ar" : "en"]}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {content.whyUs.title[isRTL ? "ar" : "en"]}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {content.whyUs.subtitle[isRTL ? "ar" : "en"]}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {content.whyUs.features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: idx * 0.15 }}
                whileHover={{ y: -12 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`} />

                <div className="relative h-full bg-card border border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                  {/* Image Section */}
                  <div className="relative h-80 overflow-hidden">
                    <motion.img
                      src={feature.image}
                      alt={feature.title.en}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.7 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />

                    {/* Icon Badge */}
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} p-5 rounded-2xl bg-card/90 backdrop-blur-xl border border-primary/30 shadow-2xl`}
                    >
                      <feature.icon className="h-10 w-10 text-primary" />
                    </motion.div>

                    {/* Number Badge */}
                    <div className={`absolute bottom-6 ${isRTL ? "left-6" : "right-6"} text-8xl font-bold text-primary/10`}>
                      0{idx + 1}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className={`p-8 ${isRTL ? "text-right" : "text-left"}`}>
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors">
                      {feature.title[isRTL ? "ar" : "en"]}
                    </h3>

                    <p className="text-muted-foreground text-lg leading-relaxed">
                      {feature.desc[isRTL ? "ar" : "en"]}
                    </p>

                    {/* Arrow Indicator */}
                    <div className={`absolute bottom-8 ${isRTL ? "left-8" : "right-8"} opacity-0 group-hover:opacity-100 transition-all duration-300`}>
                      <DirectionalArrow className="h-8 w-8 text-primary" />
                    </div>
                  </div>

                  {/* Shine Effect */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
                      isRTL
                        ? "translate-x-full group-hover:-translate-x-full"
                        : "-translate-x-full group-hover:translate-x-full"
                    } transition-transform duration-1000`}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/about">
              <Button
                size="lg"
                variant="outline"
                className="px-12 py-7 rounded-full border-2 group"
              >
                <span className="flex items-center gap-2">
                  {navLabel("اعرف المزيد عنا", "Learn More About Us")}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
      {/* ==================== ENHANCED SPORTS SHOWCASE ==================== */}
      <section ref={sportsRef} className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(var(--primary-rgb),0.08)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(var(--accent-rgb),0.08)_0%,transparent_50%)]" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Trophy className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel("رياضاتنا", "Our Sports")}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel("اكتشف عالم الرياضة", "Discover the World of Sports")}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {navLabel(
                "برامج متخصصة لجميع الأعمار والمستويات مع مدربين معتمدين دولياً",
                "Specialized programs for all ages and levels with internationally certified coaches"
              )}
            </p>
          </motion.div>

          {/* Enhanced Sports Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sports.slice(0, 6).map((sport, idx) => (
              <motion.div
                key={sport.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                onHoverStart={() => setHoveredSport(idx)}
                onHoverEnd={() => setHoveredSport(null)}
              >
                <Link to={`/sports/${sport.slug}`} className="group block h-full">
                  <motion.div
                    whileHover={{ y: -12 }}
                    className="relative h-full rounded-3xl overflow-hidden bg-card border border-border hover:border-primary/60 shadow-xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500"
                  >
                    {/* Image Container with Enhanced Overlay */}
                    <div className="relative h-80 overflow-hidden">
                      <motion.img
                        src={sport.image}
                        alt={sport.name.en}
                        className="w-full h-full object-cover"
                        animate={{
                          scale: hoveredSport === idx ? 1.15 : 1,
                        }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                      />
                      
                      {/* Multi-layer Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent" />
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                      {/* Animated Icon Badge */}
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                        className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} p-4 rounded-2xl bg-primary/95 backdrop-blur-sm border border-primary/40 shadow-2xl`}
                      >
                        <Trophy className="h-7 w-7 text-primary-foreground" />
                      </motion.div>

                      {/* Sport Level Badge */}
                      <motion.div
                        initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 + 0.3 }}
                        className={`absolute top-6 ${isRTL ? "left-6" : "right-6"} px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50`}
                      >
                        <span className="text-xs font-semibold text-muted-foreground">
                          {navLabel("جميع المستويات", "All Levels")}
                        </span>
                      </motion.div>

                      {/* Bottom Stats Overlay */}
                      <div className={`absolute bottom-6 ${isRTL ? "right-6 left-6" : "left-6 right-6"} flex items-center gap-4`}>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50">
                          <Users className="h-4 w-4 text-primary" />
                          <span className="text-sm font-semibold">
                            {sport.coaches} {navLabel("مدرب", "Coaches")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50">
                          <Star className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-semibold">4.9</span>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Content */}
                    <div className={`p-8 ${isRTL ? "text-right" : "text-left"}`}>
                      <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                        {sport.name[isRTL ? "ar" : "en"]}
                      </h3>

                      <p className="text-muted-foreground text-base line-clamp-2 mb-6 leading-relaxed">
                        {sport.description[isRTL ? "ar" : "en"]}
                      </p>

                      {/* Enhanced Footer with Features */}
                      <div className="flex items-center justify-between pt-6 border-t border-border/50">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span>{navLabel("معتمد", "Certified")}</span>
                          </div>
                        </div>

                        <motion.span
                          whileHover={{ x: isRTL ? -8 : 8 }}
                          className="text-primary font-semibold flex items-center gap-2"
                        >
                          {navLabel("اكتشف", "Explore")}
                          <DirectionalArrow className="h-5 w-5" />
                        </motion.span>
                      </div>
                    </div>

                    {/* Shine Effect */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
                        isRTL
                          ? "translate-x-full group-hover:-translate-x-full"
                          : "-translate-x-full group-hover:translate-x-full"
                      } transition-transform duration-1000`}
                    />
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/sports">
              <Button
                size="lg"
                variant="outline"
                className="px-12 py-7 rounded-full border-2 group"
              >
                <span className="flex items-center gap-2">
                  {navLabel("عرض جميع الرياضات", "View All Sports")}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== ENHANCED SUCCESS STORIES ==================== */}
      <section className="py-32 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Heart className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {content.successStories.badge[isRTL ? "ar" : "en"]}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {content.successStories.title[isRTL ? "ar" : "en"]}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {content.successStories.subtitle[isRTL ? "ar" : "en"]}
            </p>
          </motion.div>

          {/* Enhanced Stories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {content.successStories.stories.map((story, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                whileHover={{ y: -12 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />

                <div className="relative h-full rounded-3xl overflow-hidden bg-card border border-border shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500">
                  {/* Image with Enhanced Overlay */}
                  <div className="relative h-[500px] overflow-hidden">
                    <motion.img
                      src={story.image}
                      alt={story.name.en}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.08 }}
                      transition={{ duration: 0.7 }}
                    />
                    
                    {/* Multi-layer Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Content Overlay */}
                    <div className={`absolute bottom-0 left-0 right-0 p-8 ${isRTL ? "text-right" : "text-left"}`}>
                      {/* Sport & Achievement Badges */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/90 backdrop-blur-sm border border-primary/40 text-sm font-semibold text-primary-foreground shadow-lg"
                        >
                          <Trophy className="h-4 w-4" />
                          {story.sport[isRTL ? "ar" : "en"]}
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-yellow-500/90 backdrop-blur-sm border border-yellow-500/40 text-sm font-semibold text-yellow-950 shadow-lg"
                        >
                          <Medal className="h-4 w-4" />
                          {story.achievement[isRTL ? "ar" : "en"]}
                        </motion.div>
                      </div>

                      {/* Name & Role */}
                      <h3 className="text-2xl font-bold text-foreground mb-2">
                        {story.name[isRTL ? "ar" : "en"]}
                      </h3>

                      <p className="text-base text-muted-foreground mb-4">
                        {story.role[isRTL ? "ar" : "en"]}
                      </p>

                      {/* Quote */}
                      <div className="pt-4 border-t border-border/50">
                        <div className="flex items-start gap-3">
                          <div className="text-4xl text-primary/30 leading-none">"</div>
                          <p className="text-sm italic text-muted-foreground leading-relaxed pt-2">
                            {story.quote[isRTL ? "ar" : "en"]}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Star Decoration */}
                    <motion.div
                      animate={{
                        rotate: 360,
                        scale: [1, 1.2, 1],
                      }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} p-3 rounded-full bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/30`}
                    >
                      <Star className="h-6 w-6 text-yellow-500" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/achievements">
              <Button
                size="lg"
                variant="outline"
                className="px-12 py-7 rounded-full border-2 group"
              >
                <span className="flex items-center gap-2">
                  {navLabel("اكتشف المزيد من القصص", "Discover More Stories")}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== ENHANCED LATEST NEWS ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Decorative Grid */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Zap className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel("آخر الأخبار", "Latest News")}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel("ابقَ على اطلاع دائم", "Stay Always Updated")}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {navLabel(
                "آخر أخبار الأكاديمية والبطولات والفعاليات الخاصة",
                "Latest academy news, championships, and special events"
              )}
            </p>
          </motion.div>

          {/* Enhanced News Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.slice(0, 3).map((item, idx) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ y: -12 }}
              >
                <Link to={`/news/${item.slug}`} className="group block h-full">
                  <div className="relative h-full rounded-3xl overflow-hidden bg-card border border-border hover:border-primary/60 shadow-xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500">
                    {/* Image with Enhanced Effects */}
                    <div className="relative h-72 overflow-hidden">
                      <motion.img
                        src={item.image}
                        alt={item.title.en}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.7 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                      {/* Category Badge */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 + 0.2 }}
                        className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} px-4 py-2 rounded-full bg-primary/90 backdrop-blur-sm border border-primary/40 shadow-lg`}
                      >
                        <span className="text-xs font-semibold text-primary-foreground">
                          {navLabel("أخبار", "News")}
                        </span>
                      </motion.div>

                      {/* Date Badge */}
                      <div className={`absolute bottom-6 ${isRTL ? "right-6" : "left-6"} flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg`}>
                        <Calendar className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold">
                          {new Date(item.date).toLocaleDateString(
                            isRTL ? "ar-EG" : "en-US",
                            { month: "short", day: "numeric", year: "numeric" }
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Enhanced Content */}
                    <div className={`p-8 ${isRTL ? "text-right" : "text-left"}`}>
                      <h3 className="text-xl font-bold mb-4 line-clamp-2 group-hover:text-primary transition-colors duration-300 leading-tight">
                        {item.title[isRTL ? "ar" : "en"]}
                      </h3>

                      <p className="text-muted-foreground text-base line-clamp-3 mb-6 leading-relaxed">
                        {item.excerpt[isRTL ? "ar" : "en"]}
                      </p>

                      {/* Read More */}
                      <div className="flex items-center justify-between pt-6 border-t border-border/50">
                        <motion.span
                          whileHover={{ x: isRTL ? -8 : 8 }}
                          className="text-primary font-semibold flex items-center gap-2"
                        >
                          {navLabel("اقرأ المزيد", "Read More")}
                          <DirectionalArrow className="h-5 w-5" />
                        </motion.span>

                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Play className="h-4 w-4" />
                          <span>{navLabel("5 دقائق", "5 min read")}</span>
                        </div>
                      </div>
                    </div>

                    {/* Shine Effect */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
                        isRTL
                          ? "translate-x-full group-hover:-translate-x-full"
                          : "-translate-x-full group-hover:translate-x-full"
                      } transition-transform duration-1000`}
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/news">
              <Button
                size="lg"
                variant="outline"
                className="px-12 py-7 rounded-full border-2 group"
              >
                <span className="flex items-center gap-2">
                  {navLabel("جميع الأخبار", "All News")}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== ENHANCED TESTIMONIALS ==================== */}
      <section className="py-32 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 30, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Star className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel("شهادات", "Testimonials")}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel("ماذا يقول عملاؤنا؟", "What Our Clients Say?")}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {navLabel(
                "آراء حقيقية من أولياء الأمور واللاعبين حول تجربتهم معنا",
                "Real feedback from parents and players about their experience with us"
              )}
            </p>
          </motion.div>

          {/* Enhanced Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                onHoverStart={() => setActiveTestimonial(idx)}
              >
                <TestimonialCard
                  quote={testimonial.content[isRTL ? "ar" : "en"]}
                  author={testimonial.name[isRTL ? "ar" : "en"]}
                  role={testimonial.role[isRTL ? "ar" : "en"]}
                  image={testimonial.image}
                  delay={idx * 0.1}
                />
              </motion.div>
            ))}
          </div>

          {/* Rating Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="inline-flex items-center gap-6 px-10 py-6 rounded-2xl bg-card/80 backdrop-blur-xl border border-border shadow-xl">
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Star className="h-6 w-6 fill-yellow-500 text-yellow-500" />
                  </motion.div>
                ))}
              </div>
              <div className="h-12 w-px bg-border" />
              <div className={isRTL ? "text-right" : "text-left"}>
                <div className="text-3xl font-bold gradient-text">4.9/5</div>
                <div className="text-sm text-muted-foreground">
                  {navLabel("من 500+ تقييم", "From 500+ Reviews")}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== ENHANCED PARTNERS ==================== */}
      <section className="py-32 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Globe className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {navLabel("شركاؤنا", "Our Partners")}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {navLabel("شركاء النجاح العالميون", "Global Success Partners")}
            </h2>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {navLabel(
                "نفخر بشراكاتنا مع أفضل المؤسسات الرياضية المحلية والعالمية",
                "Proud of our partnerships with the best local and global sports organizations"
              )}
            </p>
          </motion.div>

          {/* Partners Grid with Enhanced Animation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <PartnerGrid partners={partners.slice(0, 12)} columns={4} />
          </motion.div>

          {/* View All Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/partners">
              <Button
                size="lg"
                variant="outline"
                className="px-12 py-7 rounded-full border-2 group"
              >
                <span className="flex items-center gap-2">
                  {navLabel("جميع الشركاء", "All Partners")}
                  <DirectionalArrow className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== ENHANCED FINAL CTA ==================== */}
      <section className="py-32 relative overflow-hidden">
        {/* Dynamic Animated Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: "url(/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background/98 via-background/95 to-background/98" />
        </div>

        {/* Animated Gradient Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary/20 via-accent/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Enhanced Badge */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary/10 border border-primary/30 backdrop-blur-xl mb-12 shadow-2xl"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Crown className="h-6 w-6 text-primary" />
              </motion.div>
              <span className="text-base font-bold text-primary tracking-wide">
                {navLabel("انضم لعائلة تشامبيونز", "Join Champions Family")}
              </span>
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Sparkles className="h-5 w-5 text-primary" />
              </motion.div>
            </motion.div>

            {/* Heading */}
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="block gradient-text"
              >
                {navLabel("هل أنت جاهز لتكون بطلًا؟", "Ready to Become a Champion?")}
              </motion.span>
            </h2>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-xl md:text-2xl text-muted-foreground mb-16 max-w-3xl mx-auto leading-relaxed"
            >
              {navLabel(
                "انضم إلى آلاف الرياضيين الذين اختاروا أكاديمية تشامبيونز لبدء رحلتهم نحو التميز والاحتراف",
                "Join thousands of athletes who chose Champions Academy to start their journey towards excellence and professionalism"
              )}
            </motion.p>

            {/* Enhanced CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <Link to="/booking">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    className="btn-glow text-lg px-16 py-8 rounded-full group shadow-2xl relative overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center gap-3">
                      <Rocket className="h-6 w-6" />
                      {navLabel("احجز جلستك الأولى", "Book Your First Session")}
                      <DirectionalArrow className="h-6 w-6 transition-transform group-hover:translate-x-2" />
                    </span>
                  </Button>
                </motion.div>
              </Link>

              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-16 py-8 rounded-full border-2 group relative overflow-hidden backdrop-blur-sm"
                  >
                    <span className="relative z-10 flex items-center gap-3">
                      <Users className="h-6 w-6" />
                      {navLabel("تواصل معنا", "Contact Us")}
                    </span>
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-wrap justify-center gap-8 mt-16"
            >
              {[
                { icon: CheckCircle2, text: navLabel("مدربون معتمدون", "Certified Coaches") },
                { icon: Shield, text: navLabel("بيئة آمنة", "Safe Environment") },
                { icon: Award, text: navLabel("50+ بطولة", "50+ Championships") },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 + idx * 0.1 }}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <item.icon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
